import React, { useState, useEffect } from "react"

import { Input, TextArea } from "../../../../components/shared/Inputs"
import Typography from "../../../../components/shared/Typography"
import { useWebsiteBuilderV2 } from "../../WebsiteBuilderV2Context"
import ConfirmSaveModal from "../shared/ConfirmSaveModal"

import EditorFooter from "./shared/EditorFooter"

const ContentEditor = ({ section, practice, onSave, onCancel, onDelete, isEditing = false }) => {
  const { handleUpdateSectionPreview, clearPreview } = useWebsiteBuilderV2()

  const [formData, setFormData] = useState({
    headline: section?.headline || "About Us",
    subheadline: section?.subheadline || "",
    body: section?.body || practice?.about || "",
    imageUrl: section?.imageUrl || "",
    settings: section?.settings || {
      layout: "text-image",
      imagePosition: "right",
      showButton: false,
      buttonText: "Learn More",
      buttonLink: ""
    }
  })
  const [errors, setErrors] = useState({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  useEffect(() => {
    if (section) {
      setFormData({
        headline: section.headline || "About Us",
        subheadline: section.subheadline || "",
        body: section.body || practice?.about || "",
        imageUrl: section.imageUrl || "",
        settings: {
          layout: "text-image",
          imagePosition: "right",
          showButton: false,
          buttonText: "Learn More",
          buttonLink: "",
          ...section.settings
        }
      })
    }
  }, [section, practice])

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }))
    }

    // Update preview immediately for content changes
    if (section?.id && ["headline", "subheadline", "body", "imageUrl"].includes(field)) {
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        [field]: value
      })
    }
  }

  const handleSettingsChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }))

    // Update preview immediately for settings changes
    if (
      section?.id &&
      [
        "layout",
        "imagePosition",
        "showButton",
        "buttonText",
        "buttonLink",
        "buttonStyle",
        "fullWidth",
        "hasBackground"
      ].includes(key)
    ) {
      const updatedSettings = { ...formData.settings, [key]: value }
      handleUpdateSectionPreview(section.id, {
        ...section,
        ...formData,
        settings: updatedSettings
      })
    }
  }

  // Close button - discard changes and close immediately
  const handleCancelClick = () => {
    clearPreview()
    onCancel()
  }

  // Confirm save from modal
  const handleConfirmSave = () => {
    setShowConfirmModal(false)
    if (validateForm()) {
      onSave({
        layout: "content",
        ...formData
      })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.headline.trim()) {
      newErrors.headline = "Section headline is required"
    }

    if (!formData.body.trim()) {
      newErrors.body = "Content is required"
    }

    if (formData.settings.showButton && !formData.settings.buttonText.trim()) {
      newErrors.buttonText = "Button text is required when button is enabled"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (validateForm()) {
      setShowConfirmModal(true)
    }
  }

  // Pre-defined content templates
  const contentTemplates = [
    {
      name: "About Practice",
      headline: "About Our Practice",
      body: `Welcome to our practice where we are dedicated to providing exceptional care in a comfortable and professional environment.

Our team of experienced professionals is committed to helping you achieve your wellness goals through personalized treatment plans and compassionate care.

We believe in building lasting relationships with our clients and providing the highest quality service every step of the way.`
    },
    {
      name: "Our Approach",
      headline: "Our Approach",
      body: `We take a holistic approach to wellness, considering not just your immediate concerns but your overall health and wellbeing.

Our evidence-based methods are tailored to your unique needs, ensuring that you receive the most effective treatment possible.

Every step of your journey is carefully planned and monitored to ensure optimal results.`
    },
    {
      name: "Why Choose Us",
      headline: "Why Choose Us",
      body: `✓ Experienced and certified professionals
✓ Personalized treatment plans
✓ State-of-the-art facilities
✓ Compassionate and caring approach
✓ Proven track record of success
✓ Convenient scheduling options`
    }
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Content */}
      <div className="space-y-4">
        <Input
          label="Section Headline"
          value={formData.headline}
          onChange={(e) => handleChange("headline", e.target.value)}
          placeholder="About Us"
          error={errors.headline}
          required
        />

        <Input
          label="Subheadline"
          value={formData.subheadline}
          onChange={(e) => handleChange("subheadline", e.target.value)}
          placeholder="Supporting text for your section"
          helperText="Optional subheading below the main headline"
        />

        <TextArea
          label="Content"
          value={formData.body}
          onChange={(e) => handleChange("body", e.target.value)}
          placeholder="Enter your content here..."
          rows={8}
          error={errors.body}
          required
          helperText="The main content of your section. You can use line breaks for paragraphs."
        />

        <Input
          label="Image URL"
          value={formData.imageUrl}
          onChange={(e) => handleChange("imageUrl", e.target.value)}
          placeholder="https://example.com/image.jpg"
          helperText="Optional image to accompany your content"
        />

        {formData.imageUrl && (
          <div className="mt-3">
            <Typography variant="small" className="mb-2 font-medium">
              Image Preview:
            </Typography>
            <img
              src={formData.imageUrl}
              alt="Content preview"
              className="h-40 w-full max-w-md rounded-lg border object-cover"
              onError={(e) => {
                e.target.style.display = "none"
              }}
            />
          </div>
        )}
      </div>

      {/* Content Templates */}
      <div className="border-t border-gray-200 pt-6">
        <Typography variant="h5" className="mb-4 font-medium">
          Quick Start Templates
        </Typography>

        <div className="space-y-3">
          <Typography variant="small" className="mb-3 text-gray-600">
            Click to use a pre-written template, then customize as needed:
          </Typography>

          {contentTemplates.map((template, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                handleChange("headline", template.headline)
                handleChange("body", template.body)
              }}
              className="w-full rounded-lg border border-gray-200 p-4 text-left transition-colors hover:border-gray-300 hover:bg-gray-50">
              <Typography variant="body" className="mb-2 font-medium">
                {template.name}
              </Typography>
              <Typography variant="small" className="line-clamp-3 text-gray-600">
                {template.body.substring(0, 150)}...
              </Typography>
            </button>
          ))}
        </div>
      </div>

      {/* Layout Settings */}
      <div className="border-t border-gray-200 pt-6">
        <Typography variant="h5" className="mb-4 font-medium">
          Layout Settings
        </Typography>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium">Content Layout</label>
            <select
              value={formData.settings.layout || "text-image"}
              onChange={(e) => handleSettingsChange("layout", e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2">
              <option value="text-only">Text Only</option>
              <option value="text-image">Text + Image</option>
              <option value="image-text">Image + Text</option>
              <option value="centered">Centered Content</option>
            </select>
          </div>

          {formData.settings.layout === "text-image" && (
            <div>
              <label className="mb-2 block text-sm font-medium">Image Position</label>
              <select
                value={formData.settings.imagePosition || "right"}
                onChange={(e) => handleSettingsChange("imagePosition", e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="left">Left</option>
                <option value="right">Right</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          )}
        </div>

        <div className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <Typography variant="body" className="font-medium">
                Add Call-to-Action Button
              </Typography>
              <Typography variant="small" className="text-gray-600">
                Include a button to drive action
              </Typography>
            </div>
            <input
              type="checkbox"
              checked={formData.settings.showButton || false}
              onChange={(e) => handleSettingsChange("showButton", e.target.checked)}
              className="h-5 w-5"
            />
          </div>

          {formData.settings.showButton && (
            <div className="ml-6 space-y-3 border-l-2 border-gray-200 pl-4">
              <Input
                label="Button Text"
                value={formData.settings.buttonText}
                onChange={(e) => handleSettingsChange("buttonText", e.target.value)}
                placeholder="Learn More"
                error={errors.buttonText}
                required={formData.settings.showButton}
              />

              <Input
                label="Button Link"
                value={formData.settings.buttonLink}
                onChange={(e) => handleSettingsChange("buttonLink", e.target.value)}
                placeholder="https://example.com or /contact"
                helperText="Where the button should link to"
              />

              <div>
                <label className="mb-2 block text-sm font-medium">Button Style</label>
                <select
                  value={formData.settings.buttonStyle || "primary"}
                  onChange={(e) => handleSettingsChange("buttonStyle", e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2">
                  <option value="primary">Primary (Filled)</option>
                  <option value="secondary">Secondary (Outline)</option>
                  <option value="text">Text Only</option>
                </select>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Typography variant="body" className="font-medium">
                Full Width Section
              </Typography>
              <Typography variant="small" className="text-gray-600">
                Extend content to full container width
              </Typography>
            </div>
            <input
              type="checkbox"
              checked={formData.settings.fullWidth || false}
              onChange={(e) => handleSettingsChange("fullWidth", e.target.checked)}
              className="h-5 w-5"
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <Typography variant="body" className="font-medium">
                Add Background
              </Typography>
              <Typography variant="small" className="text-gray-600">
                Include a subtle background color
              </Typography>
            </div>
            <input
              type="checkbox"
              checked={formData.settings.hasBackground || false}
              onChange={(e) => handleSettingsChange("hasBackground", e.target.checked)}
              className="h-5 w-5"
            />
          </div>
        </div>
      </div>

      {/* Content Preview */}
      <div className="border-t border-gray-200 pt-6">
        <Typography variant="h5" className="mb-4 font-medium">
          Content Preview
        </Typography>

        <div className="rounded-lg border bg-gray-50 p-6">
          <div className={`${formData.settings.layout === "centered" ? "text-center" : ""}`}>
            <Typography variant="h5" className="mb-2 font-bold">
              {formData.headline || "Your headline will appear here"}
            </Typography>
            {formData.subheadline && (
              <Typography variant="h5" className="mb-4 text-gray-600">
                {formData.subheadline}
              </Typography>
            )}
            <div className="prose max-w-none">
              <Typography variant="body" className="whitespace-pre-line">
                {formData.body || "Your content will appear here..."}
              </Typography>
            </div>
            {formData.settings.showButton && formData.settings.buttonText && (
              <div className="mt-6">
                <button
                  className={`rounded px-6 py-2 ${
                    formData.settings.buttonStyle === "primary"
                      ? "bg-teal-600 text-white"
                      : formData.settings.buttonStyle === "secondary"
                      ? "border border-teal-600 text-teal-600"
                      : "text-teal-600 underline"
                  }`}>
                  {formData.settings.buttonText}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <EditorFooter
        onDelete={onDelete}
        onCancel={handleCancelClick}
        onSave={handleSubmit}
        cancelLabel="Cancel"
        saveLabel={isEditing ? "Update Content" : "Add Content"}
      />

      {/* Confirm Save Modal */}
      <ConfirmSaveModal
        visible={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSave}
      />
    </form>
  )
}

export default ContentEditor
